import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronUpIcon, Loader2Icon } from 'lucide-react';
import type { Partner } from '../../types/partners';
import { usePartnerVotes } from '../../contexts/PartnerVotesContext';
import { cn } from '../../src/lib/utils';
type UpvoteButtonProps = {
  partner: Partner;
  placement: string;
  /** stacked = Product Hunt style box (cards); inline = pill (profile actions). */
  variant?: 'stacked' | 'inline';
  className?: string;
};
export function UpvoteButton({
  partner,
  placement,
  variant = 'stacked',
  className
}: UpvoteButtonProps) {
  const {
    status,
    counts,
    isVoted,
    isPending,
    errorFor,
    toggleVote
  } = usePartnerVotes();
  const reduceMotion = useReducedMotion();
  const voted = isVoted(partner.slug);
  const pending = isPending(partner.slug);
  const error = errorFor(partner.slug);
  const hasCount = status === 'ready';
  const count = counts[partner.slug] ?? 0;
  const label = voted ? `Remove your upvote for ${partner.name}${hasCount ? ` (${count} upvotes)` : ''}` : `Upvote ${partner.name}${hasCount ? ` (${count} upvotes)` : ''}`;
  const countNode = hasCount ? <span className="relative inline-flex overflow-hidden tabular-nums" data-id="element-782" data-mp="2qceh3av6ezl7">
      <AnimatePresence mode="popLayout" initial={false} data-id="element-783" data-mp="2lc0alx983xpa">
        <motion.span key={count} initial={reduceMotion ? false : {
        y: voted ? 10 : -10,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={reduceMotion ? undefined : {
        y: voted ? -10 : 10,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-784" data-mp="2gcdung20g3cs">
          {count}
        </motion.span>
      </AnimatePresence>
    </span> : <span data-id="element-785" data-mp="347qanexlik0j">Upvote</span>;
  const icon = pending ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-786" data-mp="2z9yhkhl7srur" /> : <motion.span key={voted ? 'on' : 'off'} initial={reduceMotion || !voted ? false : {
    scale: 0.6,
    y: 4
  }} animate={{
    scale: 1,
    y: 0
  }} transition={{
    type: 'spring',
    stiffness: 500,
    damping: 18
  }} className="inline-flex" data-id="element-787" data-mp="lhs4s4uy2io">
      <ChevronUpIcon className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" data-id="element-788" data-mp="1q47z6689gigl" />
    </motion.span>;
  return <div className={cn('flex flex-col', variant === 'stacked' ? 'items-center' : 'items-start', className)} data-id="element-789" data-mp="1gcbrq4kabfzp">
      <button type="button" onClick={() => toggleVote(partner, placement)} aria-pressed={voted} aria-label={label} aria-busy={pending} disabled={pending} className={cn('inline-flex items-center justify-center font-semibold transition-[background-color,border-color,color,box-shadow] duration-200', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 disabled:cursor-wait', variant === 'stacked' ? 'min-h-[60px] min-w-[60px] flex-col gap-0.5 rounded-xl border px-2 py-2 text-[13px]' : 'min-h-[44px] gap-2 rounded-lg border px-4 text-sm', voted ? 'border-lime bg-lime text-ink shadow-sm hover:bg-[#2B1E7A]' : 'border-hairline bg-panel text-text-primary hover:border-lime/40 hover:bg-[#F6F5FC] hover:text-lime')} data-id="element-790" data-mp="1lb5762j4th2i">
        {icon}
        {variant === 'inline' ? <>
            <span data-id="element-791" data-mp="3aw3x4efhh7yy">{voted ? 'Upvoted' : `Upvote ${partner.name}`}</span>
            {hasCount ? <span className={cn('rounded-md px-1.5 text-xs', voted ? 'bg-white/15' : 'bg-elevated')} data-id="element-792" data-mp="3atzkn83ir0n2">{countNode}</span> : null}
          </> : countNode}
      </button>
      <span aria-live="polite" className="sr-only" data-id="element-793" data-mp="3knzt9jy0eo0j">
        {pending ? 'Saving your vote' : ''}
      </span>
      {error ? <p role="alert" className="mt-1.5 max-w-[180px] text-center text-[11px] leading-snug text-[#B42318]" data-id="element-794" data-mp="3ukco4l5aj7wv">
          {error}
        </p> : null}
    </div>;
}